/* Shared admin UI primitives */
import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const inputCls = "w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm text-[var(--text-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--action)]";
export const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";

export const AInput = ({ label, ...p }) => (
  <div><label className={labelCls}>{label}</label><input className={inputCls} {...p} /></div>
);
export const ATextarea = ({ label, rows = 3, ...p }) => (
  <div><label className={labelCls}>{label}</label><textarea rows={rows} className={`${inputCls} h-auto py-2`} {...p} /></div>
);
export const ASelect = ({ label, options, ...p }) => (
  <div><label className={labelCls}>{label}</label>
    <select className={inputCls} {...p}>{options.map((o) => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}</select>
  </div>
);

export const Card = ({ title, action, children, className = "" }) => (
  <div className={`bg-white border border-gray-200 rounded-xl ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
        {title && <h3 className="font-head font-bold text-lg text-[var(--navy)] uppercase tracking-tight">{title}</h3>}
        {action}
      </div>
    )}
    <div className="p-5">{children}</div>
  </div>
);

export const Btn = ({ variant = "primary", loading, children, className = "", ...p }) => {
  const styles = {
    primary: "bg-[var(--red)] hover:bg-[var(--red-dark)] text-white",
    blue: "bg-[var(--action)] hover:bg-[var(--bright)] text-white",
    ghost: "bg-gray-100 hover:bg-gray-200 text-[var(--navy)]",
    navy: "bg-[var(--navy)] hover:bg-[var(--navy-800)] text-white",
    danger: "bg-white border border-red-200 text-red-600 hover:bg-red-50",
  };
  return (
    <button className={`inline-flex items-center justify-center gap-2 font-semibold text-sm px-4 py-2 rounded-md transition-colors disabled:opacity-60 ${styles[variant]} ${className}`} disabled={loading} {...p}>
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}{children}
    </button>
  );
};

export const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700", green: "bg-green-100 text-green-700",
    red: "bg-red-100 text-red-700", blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-700", navy: "bg-[var(--navy)] text-white",
  };
  return <span className={`inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full ${colors[color]}`}>{children}</span>;
};

export const EmptyState = ({ icon: Icon, title, hint }) => (
  <div className="text-center py-16 text-gray-400">
    {Icon && <Icon className="w-10 h-10 mx-auto mb-3 opacity-50" />}
    <p className="font-semibold text-gray-500">{title}</p>
    {hint && <p className="text-sm mt-1">{hint}</p>}
  </div>
);

export const useSave = () => {
  const [saving, setSaving] = useState(false);
  const run = async (fn, msg = "Saved") => {
    setSaving(true);
    try { await fn(); toast.success(msg); }
    catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    finally { setSaving(false); }
  };
  return [saving, run];
};
